<template>
  <div class="custom-multiselect" :class="containerClass">
    <div class="multiselect-input" :class="inputClass" @click="focusInput">
      <div class="selected-items">
        <span 
          v-for="(item, index) in selectedItems" 
          :key="index"
          class="selected-tag"
          :class="tagClass"
        >
          {{ item.text }}
          <span class="remove-icon" @click.stop="removeItem(item)">×</span>
        </span>
        <input 
          v-if="isOpen || searchQuery"
          type="text" 
          class="inline-search"
          v-model="searchQuery"
          @keydown="handleKeydown"
          @blur="handleBlur"
          ref="searchInput"
          :placeholder="selectedItems.length === 0 ? placeholder : 'Search...'"
        >
        <span v-if="selectedItems.length === 0 && !isOpen && !searchQuery" class="placeholder">
          {{ placeholder }}
        </span>
      </div>
      <span class="dropdown-arrow" :class="{ 'open': isOpen }" @click="toggleDropdown">▼</span>
    </div>
    
    <div v-if="isOpen" class="dropdown-options">
      <!-- Action buttons for Select All and Clear All -->
      <div class="action-buttons" v-if="multiple && options.length > 0">
        <button 
          type="button" 
          class="action-btn select-all-btn" 
          @click="selectAll"
          :disabled="allSelected"
        >
          Select All
        </button>
        <button 
          type="button" 
          class="action-btn clear-all-btn" 
          @click="clearAll"
          :disabled="selectedItems.length === 0"
        >
          Clear All
        </button>
      </div>
      
      <div 
        v-for="option in filteredOptions" 
        :key="option.value"
        class="dropdown-option"
        :class="{ 'selected': isSelected(option) }"
        @click="toggleOption(option)"
      >
        <span class="option-text">{{ option.text }}</span>
        <span v-if="isSelected(option)" class="check-icon">✓</span>
      </div>
      <div v-if="filteredOptions.length === 0" class="no-results">
        No options found
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CustomMultiSelect",
  props: {
    options: {
      type: Array,
      default: () => []
    },
    selectedOptions: {
      type: [Array, String],
      default: () => []
    },
    placeholder: {
      type: String,
      default: "Select options"
    },
    multiple: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errorClass: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      isOpen: false,
      selectedItems: [],
      searchQuery: ''
    };
  },
  computed: {
    filteredOptions() {
      return this.options.filter(option => {
        // First filter out already selected items
        const notSelected = !this.isSelected(option);
        
        // Then filter by search query
        const matchesSearch = this.searchQuery === '' || 
          option.text.toLowerCase().includes(this.searchQuery.toLowerCase());
        
        return notSelected && matchesSearch;
      });
    },
    allSelected() {
      return this.options.length > 0 && this.selectedItems.length === this.options.length;
    },
    containerClass() {
      return {
        'disabled': this.disabled,
        'error': this.errorClass === 'is-invalid'
      };
    },
    inputClass() {
      return {
        'error': this.errorClass === 'is-invalid'
      };
    },
    tagClass() {
      return {
        'error': this.errorClass === 'is-invalid'
      };
    }
  },
  watch: {
    selectedOptions: {
      handler(newVal) {
        // Handle both array and JSON string inputs
        if (typeof newVal === 'string') {
          try {
            this.selectedItems = JSON.parse(newVal);
          } catch (e) {
            console.warn('Invalid JSON string in selectedOptions:', newVal);
            this.selectedItems = [];
          }
        } else if (Array.isArray(newVal)) {
          this.selectedItems = [...newVal];
        } else {
          this.selectedItems = [];
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    // Handle both array and JSON string inputs
    if (typeof this.selectedOptions === 'string') {
      try {
        this.selectedItems = JSON.parse(this.selectedOptions);
      } catch (e) {
        console.warn('Invalid JSON string in selectedOptions:', this.selectedOptions);
        this.selectedItems = [];
      }
    } else if (Array.isArray(this.selectedOptions)) {
      this.selectedItems = [...this.selectedOptions];
    } else {
      this.selectedItems = [];
    }
    document.addEventListener('click', this.closeDropdown);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeDropdown);
  },
  methods: {
    toggleDropdown() {
      if (!this.disabled) {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
          this.$nextTick(() => {
            if (this.$refs.searchInput) {
              this.$refs.searchInput.focus();
            }
          });
        } else {
          this.searchQuery = '';
        }
      }
    },
    focusInput() {
      if (!this.disabled) {
        this.isOpen = true;
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },
    handleKeydown(event) {
      // Handle escape key to close dropdown
      if (event.key === 'Escape') {
        this.isOpen = false;
        this.searchQuery = '';
        event.target.blur();
      }
      // Handle enter key to prevent form submission
      else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.filteredOptions.length === 1) {
          this.toggleOption(this.filteredOptions[0]);
        }
      }
      // Handle backspace to remove last item if search is empty
      else if (event.key === 'Backspace' && this.searchQuery === '' && this.selectedItems.length > 0) {
        this.removeItem(this.selectedItems[this.selectedItems.length - 1]);
      }
    },
    handleBlur(event) {
      // Delay closing to allow for option clicks
      setTimeout(() => {
        if (!this.$el.contains(document.activeElement)) {
          this.isOpen = false;
          this.searchQuery = '';
        }
      }, 150);
    },
    closeDropdown(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
        this.searchQuery = '';
      }
    },
    toggleOption(option) {
      if (this.multiple) {
        const index = this.selectedItems.findIndex(item => item.value === option.value);
        if (index > -1) {
          this.removeItem(option);
        } else {
          this.addItem(option);
        }
      } else {
        this.selectedItems = [option];
        this.isOpen = false;
        this.emitChange();
      }
    },
    addItem(option) {
      this.selectedItems.push(option);
      this.emitChange();
    },
    removeItem(option) {
      const index = this.selectedItems.findIndex(item => item.value === option.value);
      if (index > -1) {
        this.selectedItems.splice(index, 1);
        this.emitChange();
      }
    },
    isSelected(option) {
      return this.selectedItems.some(item => item.value === option.value);
    },
    emitChange() {
      this.$emit('select', this.selectedItems);
      this.$emit('input', this.selectedItems);
    },
    selectAll() {
      if (this.multiple && this.options.length > 0) {
        this.selectedItems = [...this.options];
        this.emitChange();
      }
    },
    clearAll() {
      if (this.multiple) {
        this.selectedItems = [];
        this.emitChange();
      }
    }
  }
};
</script>

<style scoped>
.custom-multiselect {
  position: relative;
  width: 100%;
}

.multiselect-input {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  background-color: #fff;
  min-height: 38px;
  padding: 0.375rem 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.multiselect-input:hover {
  border-color: #80bdff;
}

.multiselect-input:focus {
  outline: 0;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.multiselect-input.error {
  border-color: #dc3545;
  background-color: #f8d7da;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  flex: 1;
  align-items: center;
  min-height: 1.5rem;
}

.inline-search {
  border: none;
  outline: none;
  background: transparent;
  padding: 0.25rem 0;
  margin: 0.125rem;
  font-size: 0.875rem;
  color: #495057;
  min-width: 100px;
  flex: 1;
}

.inline-search::placeholder {
  color: #6c757d;
  font-style: italic;
}

.selected-tag {
  display: inline-flex;
  align-items: center;
  background-color: #007bff;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  margin: 0.125rem;
}

.selected-tag.error {
  background-color: #dc3545;
  border: 1px solid #dc3545;
}

.remove-icon {
  margin-left: 0.5rem;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.1em;
  padding: 0.1rem 0.3rem;
  border-radius: 50%;
  transition: background-color 0.15s ease-in-out;
}

.remove-icon:hover {
  background-color: rgba(255, 255, 255, 0.3);
  color: #ffcccc;
}

.placeholder {
  color: #6c757d;
  font-style: italic;
}

.dropdown-arrow {
  margin-left: 0.1rem;
  transition: transform 0.15s ease-in-out;
  color: #6c757d;
  font-size: 0.8em;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.dropdown-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ced4da;
  border-top: none;
  border-radius: 0 0 0.25rem 0.25rem;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.action-buttons {
  display: flex;
  gap: 0.1rem;
  padding: 0.1rem;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
  border-radius: 0.25rem 0.25rem 0 0;
}

.action-btn {
  flex: 1;
  padding: 0.1rem 0.1rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  background-color: #fff;
  color: #495057;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.action-btn:hover:not(:disabled) {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #f8f9fa;
}

.select-all-btn:not(:disabled):hover {
  background-color: #d4edda;
  border-color: #28a745;
  color: #155724;
}

.clear-all-btn:not(:disabled):hover {
  background-color: #f8d7da;
  border-color: #dc3545;
  color: #721c24;
}

.dropdown-option {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.15s ease-in-out;
}

.dropdown-option:hover {
  background-color: #f8f9fa;
}

.dropdown-option.selected {
  background-color: #e3f2fd;
  color: #1976d2;
}

.option-text {
  flex: 1;
}

.check-icon {
  color: #28a745;
  font-weight: bold;
  margin-left: 0.5rem;
}

.no-results {
  padding: 0.75rem;
  text-align: center;
  color: #6c757d;
  font-style: italic;
  background-color: #f8f9fa;
}

.disabled .multiselect-input {
  background-color: #e9ecef;
  cursor: not-allowed;
  opacity: 0.65;
}

.disabled .dropdown-arrow {
  color: #6c757d;
}

/* Focus states */
.custom-multiselect:focus-within .multiselect-input {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.custom-multiselect.error:focus-within .multiselect-input {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}
</style>



// WEBPACK FOOTER //
// CustomMultiSelect.vue?9655f200